import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LangContext";
import { useContent, resolveUrl } from "@/cms/ContentContext";

const Nav = () => {
  const { t, lang, setLang } = useLang();
  const { content, loading } = useContent();
  const LOGO = resolveUrl(content?.images?.logo) || "";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "#inicio", label: t.nav.inicio },
    { id: "#sobre", label: t.nav.sobre },
    { id: "#servicos", label: t.nav.servicos },
    { id: "#portfolio", label: t.nav.portfolio },
    { id: "#testemunhos", label: t.nav.testemunhos },
    { id: "#contacto", label: t.nav.contacto },
  ];

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F5F2EC]/85 backdrop-blur-xl border-b border-[#3A3524]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-72 md:h-96 flex items-center justify-between">
        {/* Logo mark — only rendered once CMS content is loaded to avoid flashing an old default */}
        <a href="#inicio" data-testid="nav-logo" className="flex items-center gap-3 group">
          {LOGO ? (
            <img
              src={LOGO}
              alt="ORDO by Isa"
              className="h-64 md:h-[22rem] w-auto object-contain mix-blend-multiply"
            />
          ) : (
            <span
              aria-hidden={!loading}
              className="inline-block h-64 md:h-[22rem] w-40 md:w-56"
            />
          )}
        </a>

        {/* Links (desktop) */}
        <nav className="hidden lg:flex items-center gap-9 text-[13px] tracking-[0.18em] uppercase font-sans">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.id}
              data-testid={`nav-link-${l.id.slice(1)}`}
              className="hover-underline text-[#3A3524]/85 hover:text-[#3A3524] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-4 md:gap-6">
          <div
            data-testid="lang-toggle"
            className="hidden sm:flex items-center gap-1 text-[11px] tracking-[0.2em] font-sans"
          >
            <button
              data-testid="lang-pt"
              onClick={() => setLang("pt")}
              className={`px-1 py-0.5 transition-colors ${
                lang === "pt" ? "text-[#3A3524]" : "text-[#3A3524]/40 hover:text-[#3A3524]/70"
              }`}
            >
              PT
            </button>
            <span className="text-[#3A3524]/30">/</span>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-1 py-0.5 transition-colors ${
                lang === "en" ? "text-[#3A3524]" : "text-[#3A3524]/40 hover:text-[#3A3524]/70"
              }`}
            >
              EN
            </button>
          </div>

          <a
            href="#contacto"
            data-testid="nav-cta"
            className="hidden sm:inline-flex items-center gap-2 border border-[#3A3524]/70 text-[#3A3524] hover:bg-[#3A3524] hover:text-[#F5F2EC] transition-all duration-500 px-5 py-2.5 text-[11px] tracking-[0.25em] uppercase"
          >
            {t.nav.cta}
            <span aria-hidden>→</span>
          </a>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden flex flex-col gap-1.5 p-2"
            aria-label="Menu"
          >
            <span className={`block w-6 h-px bg-[#3A3524] transition-transform ${open ? "translate-y-[7px] rotate-45" : ""}`} />
            <span className={`block w-6 h-px bg-[#3A3524] transition-opacity ${open ? "opacity-0" : ""}`} />
            <span className={`block w-6 h-px bg-[#3A3524] transition-transform ${open ? "-translate-y-[7px] -rotate-45" : ""}`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.5, ease: [0.7, 0, 0.15, 1] }}
        className="lg:hidden overflow-hidden bg-[#F5F2EC]/95 backdrop-blur-xl border-t border-[#3A3524]/10"
      >
        <div className="px-6 py-8 flex flex-col gap-5">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.id}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-link-${l.id.slice(1)}`}
              className="font-serif text-3xl text-[#3A3524]"
            >
              {l.label}
            </a>
          ))}
          <div className="flex items-center gap-3 mt-4 text-[12px] tracking-[0.2em]">
            <button
              onClick={() => setLang("pt")}
              className={lang === "pt" ? "text-[#3A3524]" : "text-[#3A3524]/50"}
            >
              PT
            </button>
            <span className="text-[#3A3524]/30">/</span>
            <button
              onClick={() => setLang("en")}
              className={lang === "en" ? "text-[#3A3524]" : "text-[#3A3524]/50"}
            >
              EN
            </button>
          </div>
        </div>
      </motion.div>
    </motion.header>
  );
};

export default Nav;
